import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { catchError, defer, distinctUntilChanged, EMPTY, filter, finalize, iif, map, merge, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from 'src/core/services/modal.service';
import { ApiResponse } from 'src/core/types/general.types';
import { PSMData, WindowDay } from 'src/core/types/systemhealth.types';
import { SortEvent } from 'primeng/api';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSystem } from 'src/core/types/selectedSystem.types';
import { PsmSiteResponse } from 'src/core/types/servicehistory.types';
import type { Observable } from 'rxjs';


@Component({
  selector: 'app-psm-details',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './psm-details.component.html',
  styleUrl: './psm-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PsmDetailsComponent {


  //Table Variables
  @ViewChild('table') table: Table;

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private modalService: ModalService,
    private selectedContentService: SelectedContentService
  ) { }

  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  isDeviceUI: boolean;
  deviceInformation: any;
  psms;
  expandedRows: any = {};
  searchTerm: string = '';
  selectedRange: number;

  @Input() tabs: any[] = [];
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
   onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }  
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadingService.$isPSMLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isPSMError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);

      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeServiceHistory;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$?.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshPSMs.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isPSMLoading.next(true);
        this.loadingService.$isPSMError.next(false);
        this.loadingService.$isPSMEmpty.next(false);
        this.psms = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.proasPullDate;
        const pulledFor = this.selectedSystemService.proasPulledFor;

        const isDevice = this.selectedContentService.selection.kind == "device";
        this.isDeviceUI = isDevice;
        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        const funcloc = this.selectedContentService.currentSite?.Id;

        const cacheKey = isDevice ? (serial + material) : funcloc;

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== cacheKey;

        type ProasResponse = PSMData | PsmSiteResponse;

        const apiCall$: Observable<ApiResponse<ProasResponse>> = isDevice
          ? this.dataService.getPSMs(material, serial).pipe(
            map((result): ApiResponse<ProasResponse> => result)
          )
          : this.dataService.getPSMsSite(funcloc).pipe(
            map((result): ApiResponse<ProasResponse> => result)
          );

        return iif(
          () => shouldFetch,
          apiCall$.pipe(
            tap(({ response, status }: ApiResponse<ProasResponse>) => {
              console.log(force ? 'Forced refresh of psm data' : 'Fetching new psm data');
              this.selectedSystemService.proasPullDate = now;
              this.selectedSystemService.proasPulledFor = cacheKey;
              if (status === 200) {
                this.selectedSystemService.$proas.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$proas.next(null);
                this.loadingService.$isPSMEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$proas.pipe(
            take(1),
            tap(() => console.log('Using cached psm data')),
            map((cached): ApiResponse<ProasResponse> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response }: ApiResponse<ProasResponse>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          const range = rangeValue === 365 ? 120 as WindowDay : rangeValue as WindowDay;

          if (!response) {
            this.psms = [];
            this.loadingService.$isPSMEmpty.next(true);
            this.loadingService.$isPSMLoading.next(false);
            return;
          }

          if (this.selectedContentService.selection.kind == "device") {
            let temp = response as PSMData;
            this.psms = temp ? temp?.['psms']?.filter(psm => psm['time_windows'].includes(range))?.map(
              psm => ({...psm, serial: temp.serial_number })
            ) : [];
          }

          if (this.selectedContentService.selection.kind == "site") {
            let temp = response as PsmSiteResponse;
            
            const mapped = temp.psms ? temp.psms.filter(psm => psm['time_windows'].includes(range)).map(
                psm => ({...psm, serial: temp.serial_number })
              ) : [];

            this.psms = mapped;

          }


          this.loadingService.$isPSMLoading.next(false);

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.psms = [];
          this.loadingService.$isPSMError.next(true);
          this.loadingService.$isPSMLoading.next(false);
        }
      }
    );

  }

  onSearchApply(event: any) {
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  refresh() {
    this.selectedSystemService.$refreshPSMs.next();
  }

  openPSMDetails(pattern_id) {
    this.selectedSystemService.$selectedPattern.next(pattern_id);
    this.modalService.$modalChanged.next({ modal: 'psm', open: true });
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'target': // assuming this is a date field
          val1 = new Date(a.target_date);
          val2 = new Date(b.target_date);
          break;
        case 'score': // assuming this is a date field
          val1 = new Date(a.score_date);
          val2 = new Date(b.score_date);
          break;

        case 'serial': // string field
          val1 = a.serial_number?.toLowerCase() ?? '';
          val2 = b.serial_number?.toLowerCase() ?? '';
          break;

        case 'id': // string field
          val1 = a.id?.toLowerCase() ?? '';
          val2 = b.id?.toLowerCase() ?? '';
          break;
        case 'status': // string field
          val1 = a.status?.toLowerCase() ?? '';
          val2 = b.status?.toLowerCase() ?? '';
          break;
        case 'code': // string field
          val1 = a.pattern_code?.toLowerCase() ?? '';
          val2 = b.pattern_code?.toLowerCase() ?? '';
          break;
        case 'name': // string field
          val1 = a.pattern_name?.toLowerCase() ?? '';
          val2 = b.pattern_name?.toLowerCase() ?? '';
          break;
        case 'notificationid': // string field
          val1 = a.notification_id?.toLowerCase() ?? '';
          val2 = b.notification_id?.toLowerCase() ?? '';
          break;
        case 'peakticketid': // string field
          val1 = a.peak_ticket_id?.toLowerCase() ?? '';
          val2 = b.peak_ticket_id?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

}
